import { Link, useParams } from "react-router-dom";
import { Badge, Card, CardBody, CardHeader, DataTable, StudioState } from "@odingard/design-system";
import { useAsync } from "../../lib/useAsync";
import { useAuth } from "../../lib/auth";
import { sopApi } from "../../lib/api";
import { GovernedAction } from "../../governance/GovernedAction";

export function SopProcedurePage() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const { procedureId = "" } = useParams();
  const state = useAsync(async () => {
    const [procedure, versions] = await Promise.all([
      sopApi.procedure(orgId, procedureId),
      sopApi.versions(orgId, procedureId),
    ]);
    return { procedure: procedure.data.procedure, versions: versions.data.versions };
  }, [orgId, procedureId]);

  if (state.loading) return <StudioState state="loading" description="Loading the procedure…" />;
  if (state.error)
    return <StudioState state="error" description={state.error.message} onRetry={state.reload} />;
  if (!state.data) return null;
  const { procedure, versions } = state.data;
  const retired = procedure.lifecycleState === "RETIRED";

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{procedure.title}</h1>
          <p>{procedure.purpose ?? "No purpose recorded."}</p>
        </div>
        <div className="ods-actions">
          <Badge tone={retired ? "neutral" : "success"}>{procedure.lifecycleState}</Badge>
          <GovernedAction
            permission="procedures.author"
            label="Draft a new version"
            title="Draft a new version"
            description="A new draft version is authored from the current procedure. Published versions are immutable, so a change is always a new version."
            confirmLabel="Create draft"
            requireRationale
            rationaleLabel="Change reason"
            rationaleHelp="What is changing, and why."
            triggerVariant="primary"
            onConfirm={(changeReason) =>
              sopApi.createVersion(orgId, procedure.id, {
                expectedProcedureVersion: procedure.version,
                changeReason,
              })
            }
            onDone={state.reload}
          />
          {!retired && (
            <GovernedAction
              permission="procedures.approve"
              label="Retire"
              title="Retire this procedure"
              destructive
              consequence="No further run can be started against this procedure once it is retired."
              confirmLabel="Retire"
              requireRationale
              rationaleLabel="Reason"
              onConfirm={(reason) =>
                sopApi.retireProcedure(orgId, procedure.id, reason, procedure.version)
              }
              onDone={state.reload}
            />
          )}
        </div>
      </div>
      <Card>
        <CardHeader title="Versions" />
        <CardBody>
          {versions.length === 0 ? (
            <StudioState
              state="empty"
              title="No versions yet"
              description="Draft a version to author the steps this procedure requires."
            />
          ) : (
            <DataTable
              rows={versions}
              caption="Procedure versions"
              columns={[
                {
                  key: "version",
                  header: "Version",
                  render: (version) => (
                    <Link to={`/studio/sop-ai/procedures/${procedure.id}/versions/${version.id}`}>
                      Version {version.versionNumber}
                    </Link>
                  ),
                },
                {
                  key: "state",
                  header: "State",
                  render: (version) => (
                    <Badge tone={version.state === "PUBLISHED" ? "success" : "neutral"}>
                      {version.state}
                    </Badge>
                  ),
                },
                {
                  key: "reason",
                  header: "Change reason",
                  render: (version) => version.changeReason ?? "Not recorded",
                },
                {
                  key: "effective",
                  header: "Effective from",
                  render: (version) => version.effectiveFrom ?? "Not effective",
                },
              ]}
            />
          )}
        </CardBody>
      </Card>
    </>
  );
}
